// Blog post registry. Posts are stored in-code so they're git-versioned,
// crawlable, and don't require a CMS. Add new entries to POSTS; each post
// is keyed by its URL slug.
//
// Block schema:
//   { type: "p", text }
//   { type: "h2", text }
//   { type: "h3", text }
//   { type: "ul", items: [string] }
//   { type: "quote", text, author? }
//   { type: "callout", title?, text }   — highlighted CTA / takeaway block

export const POSTS = [
  {
    slug: "best-dog-parks-tampa-bay",
    title: "Best Dog Parks in Tampa Bay (2026 Guide)",
    excerpt:
      "From off-leash favorites to hidden small-dog gems — a local's guide to the best dog parks across Tampa, St. Petersburg, Riverview, Brandon, and beyond.",
    author: "Cesar Peña",
    authorTitle: "Founder of Pawground",
    publishedAt: "2026-05-21",
    updatedAt: "2026-05-21",
    readMinutes: 8,
    coverImage: "/founder-cesar-jacob.png",
    coverAlt: "Cesar Peña, founder of Pawground, with his pit mix Jacob",
    body: [
      {
        type: "p",
        text:
          "If you're a dog parent in Tampa Bay, you know the struggle — you want to take your pup somewhere fun, but you're not sure which parks are actually worth the drive. Are they fenced? Is there shade? Will it be packed or empty?",
      },
      {
        type: "p",
        text:
          "I'm Cesar, and my pit mix Jacob and I have been exploring dog parks across Tampa Bay for over a year. Here are the best spots we've found, from downtown Tampa to the suburbs.",
      },

      { type: "h2", text: "1. Davis Islands Dog Beach — Best for Water-Loving Dogs" },
      { type: "p", text: "Location: 1002 Severn Ave, Tampa, FL 33606" },
      {
        type: "p",
        text:
          "This is one of Tampa's most iconic dog spots and for good reason. It's a dog beach and dog park in one — your pup can splash in Tampa Bay off-leash on the sand side, or play on the fenced grassy area if they prefer staying dry. It gets crowded on weekends, so try to visit on weekday mornings for more space. There's not a ton of shade, so bring water and keep an eye on your dog during the hotter months.",
      },
      { type: "ul", items: [
        "Good for: Dogs who love water, socializing, weekend outings",
        "Heads up: Limited shade, busy on weekends, no separate small dog area on the beach side",
      ]},

      { type: "h2", text: "2. Gadsden Park Dog Park — Best All-Around Park" },
      { type: "p", text: "Location: 6901 S MacDill Ave, Tampa, FL 33611" },
      {
        type: "p",
        text:
          "Gadsden is a favorite among South Tampa dog owners. It has separate areas for small and large dogs, real grass (not turf), water bowls, kiddie pools, and plenty of shade from mature trees. It's well-maintained and spacious enough that it doesn't feel overcrowded even during peak hours.",
      },
      { type: "ul", items: [
        "Good for: All dog sizes, first-time dog park visits, regular weekly visits",
        "Heads up: Can get muddy after rain",
      ]},

      { type: "h2", text: "3. Curtis Hixon Waterfront Park Dog Park — Best Downtown Park" },
      { type: "p", text: "Location: 600 N Ashley Dr, Tampa, FL 33602" },
      {
        type: "p",
        text:
          "Right on the Tampa Riverwalk, this park has artificial turf, agility equipment, and drinking water for dogs and humans. It's fully fenced with gorgeous views of the Hillsborough River. Great if you want to combine a dog park visit with a walk along the Riverwalk or lunch at a nearby restaurant.",
      },
      { type: "ul", items: [
        "Good for: Active dogs who like agility obstacles, downtown outings",
        "Heads up: Tends to get busy after work hours (5–7 PM)",
      ]},

      { type: "h2", text: "4. Al Lopez Park Dog Park — Best Near the Airport" },
      { type: "p", text: "Location: 4810 N Himes Ave, Tampa, FL 33614" },
      {
        type: "p",
        text:
          "At about 5 acres, this is one of the biggest dog parks in Tampa. It has double gates for safe entry, separate small and large dog areas, a dog wash station, water fountains, sheltered picnic areas, and a playground nearby if you've got kids too. It's close to Tampa International Airport, making it a great stop before or after a flight.",
      },
      { type: "ul", items: [
        "Good for: Large dogs who need room to run, families with kids",
        "Heads up: Very busy after 5 PM on weekdays",
      ]},

      { type: "h2", text: "5. Julian B. Lane Riverfront Park Dog Park — Most Scenic" },
      { type: "p", text: "Location: 1001 W Platt St, Tampa, FL 33606" },
      {
        type: "p",
        text:
          "This 25-acre waterfront park sits right on the Hillsborough River across from downtown. The dog area has agility equipment and plenty of space, and the park itself has a splash pad, boat house, and beautiful walking paths. It's one of the newer parks in Tampa and it shows — everything feels clean and modern.",
      },
      { type: "ul", items: [
        "Good for: Dogs and owners who want more than just a dog park, scenic walks",
        "Heads up: Parking can fill up on weekends",
      ]},

      { type: "h2", text: "6. Palma Ceia Dog Park — Best for Small Dogs" },
      { type: "p", text: "Location: 2200 Marti St, Tampa, FL 33629" },
      {
        type: "p",
        text:
          "If you've got a smaller pup, this is your spot. It's a quieter, no-frills park with plenty of shade from a canopy of oak trees, which is a blessing in the Florida heat. There's drinking water available, and the crowd tends to be smaller and more relaxed than the bigger parks.",
      },
      { type: "ul", items: [
        "Good for: Small dogs, older dogs, dogs who get overwhelmed in big crowds",
        "Heads up: Limited parking",
      ]},

      { type: "h2", text: "7. Saladino Park Dog Park (Brandon) — Best in the Suburbs" },
      { type: "p", text: "Location: 1705 Bryan Rd, Brandon, FL 33510" },
      {
        type: "p",
        text:
          "This is the go-to park for dog owners in the Brandon and Riverview area. It's just under 3 acres with a fountain, shaded seating, and separate areas for different dog sizes. It's well-maintained by Hillsborough County and usually has a friendly, regular crowd.",
      },
      { type: "ul", items: [
        "Good for: Dog parents in Brandon, Riverview, and Valrico",
        "Heads up: Can get warm in the afternoon — morning visits are best",
      ]},

      { type: "h2", text: "8. Epperson Dog Park (Wesley Chapel) — Best in Wesley Chapel" },
      { type: "p", text: "Location: 31747 Overpass Rd, Wesley Chapel, FL 33543" },
      {
        type: "p",
        text:
          "Wesley Chapel dog parents, this one's for you. Epperson Dog Park is fully fenced with separate areas for big and small dogs. It's free to use and has seating so you can relax while your pup plays. It's a newer park in a growing area, so it doesn't get as packed as the Tampa parks.",
      },
      { type: "ul", items: [
        "Good for: Wesley Chapel and New Tampa residents, dogs who need space",
        "Heads up: Limited shade and water — bring your own supplies",
      ]},

      { type: "h2", text: "Tips for Visiting Tampa Bay Dog Parks" },
      { type: "ul", items: [
        "Go early. Florida heat is no joke, especially from May through October. Morning visits (before 10 AM) are cooler and less crowded.",
        "Bring water. Not every park has working water fountains. A collapsible bowl and a water bottle can save the day.",
        "Check the vibe first. Before unleashing your dog, take a minute to watch how the other dogs are playing. If the energy feels too rough, it's okay to wait or try another park.",
        "Keep vaccinations current. Most parks require up-to-date rabies and distemper vaccines. Some, like Two Shepherds Taproom, also require bordetella and proof of spay/neuter.",
        "Clean up after your pup. Bring extra bags just in case the dispensers are empty.",
      ]},

      {
        type: "callout",
        title: "Find Your Dog's Next Favorite Park on Pawground",
        text:
          "Want to know which parks are busy right now? Or save your favorites and check in when you arrive? Pawground is a free app built for Tampa Bay dog parents. You can search for dog parks, read reviews from real dog owners, see live check-ins, and discover dog-friendly restaurants, beaches, groomers, vets, and more.",
      },

      {
        type: "p",
        text:
          "Cesar Peña is the founder of Pawground and a dog dad in Riverview, FL. He and his pit mix Jacob are on a mission to help every Tampa Bay dog parent find the right places for their pups.",
      },
    ],
  },
];

export const getPost = (slug) => POSTS.find((p) => p.slug === slug) || null;
